import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../config/api-client";
import { API_ENDPOINTS } from "../config/api-endpoints";
import { QueryKeys } from "../config/query-keys";
import type { ProductDetailData } from "./use-product-detail";

/**
 * Favori ürün API'si.
 *
 * Tek path iki iş görür: `GET` sayfalanmış liste, `POST` ekle/çıkar. Liste
 * satırları ürün detayıyla aynı gövdede döner (her satır tam hidrasyon), bu
 * yüzden backend sayfa başına 50 satırda sınırlıyor.
 */

/** Favori listesi satırı — ürün detayıyla aynı gövde, `isFavorite` hep true. */
export type FavoriteProduct = ProductDetailData;

export interface FavoriteProductsMeta {
  page: number;
  limit: number;
  total: number;
  /** Bu uçta `totalPages` — paylaşılan `PaginationMeta`'daki `totalPage` değil. */
  totalPages: number;
}

export interface FavoriteProductsResponse {
  statusCode: number;
  success: boolean;
  data: {
    result: FavoriteProduct[];
    meta: FavoriteProductsMeta;
  };
}

export interface SetFavoriteProductRequest {
  /** Product ObjectId (Typesense belgesindeki `productId`). */
  productId: string;
  action: "add" | "remove";
}

export interface SetFavoriteProductResponse {
  statusCode: number;
  success: boolean;
  data: {
    success: boolean;
    /** Çağrı sonrası oluşan durum — iyimser kalbin uzlaşacağı değer. */
    isFavorite: boolean;
  };
}

/** Sayfa başına satır. Backend tavanı 50; her satır ürün detayı kadar pahalı. */
export const FAVORITES_PAGE_SIZE = 20;

interface UseFavoriteProductsParams {
  page?: number;
  limit?: number;
  /** Yalnızca `createdAt` destekleniyor; varsayılan en yeniden eskiye. */
  sortBy?: string;
  enabled?: boolean;
}

/**
 * Kullanıcının favori ürünleri (sayfalı).
 *
 * `enabled` ile oturumsuz çağrı engellenir — uç `AuthGuard` altında, misafir
 * için 401'den başka bir şey dönmez.
 */
export const useFavoriteProducts = ({
  page = 1,
  limit = FAVORITES_PAGE_SIZE,
  sortBy,
  enabled = true,
}: UseFavoriteProductsParams = {}) => {
  return useQuery<FavoriteProductsResponse>({
    queryKey: QueryKeys.FAVORITE_PRODUCTS.list(page, limit, sortBy),
    queryFn: () =>
      apiClient.get<FavoriteProductsResponse>(API_ENDPOINTS.favoriteProducts, {
        params: { page, limit, ...(sortBy ? { sortBy } : {}) },
      }),
    enabled,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Favoriye ekle / favoriden çıkar. İki yönde de idempotent.
 *
 * İyimser kalp durumu `favorite-products-store`'da tutulur (bkz.
 * `useFavoriteToggle`); burada yalnızca liste sorgusu tazelenir.
 */
export const useSetFavoriteProduct = () => {
  const queryClient = useQueryClient();

  return useMutation<
    SetFavoriteProductResponse,
    unknown,
    SetFavoriteProductRequest
  >({
    mutationFn: (body) =>
      apiClient.post<SetFavoriteProductResponse>(
        API_ENDPOINTS.favoriteProducts,
        body,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QueryKeys.FAVORITE_PRODUCTS.all,
      });
    },
  });
};
